// GlitchSignature — glitch effect ported from the Framer module
// fSmVhEA1Le3v2nbFCEeM/ur9mtw4ZFHR4QeEUz5w7 (GlithTitle) and adapted to work
// with an SVG artwork instead of text.
//
// The original component slices TEXT into horizontal strips (clip-path) and
// renders RGB copies with `mix-blend-mode: screen`. Here the artwork is SVG
// paths, so the paths live once in <defs> and every strip/layer references
// them with <use> — same visual result, no duplicated path data in the DOM.
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import DrawOnScroll from './DrawOnScroll';

const DEFS_ID = 'sr-glitch-signature';

export default function GlitchSignature({
    paths,
    viewBoxWidth,
    viewBoxHeight,
    strokeColor = '#ffffff',
    fillColor = '#ffffff',
    fillEnabled = true,
    strokeWidth = 2.6,
    drawProps = {},
    intensity = 6,
    sliceCount = 6,
    colorGlitchOne = '#ff0044',
    colorGlitchTwo = '#00ff9d',
    colorGlitchThree = '#0088ff',
    style
}) {
    const [glitchReady, setGlitchReady] = useState(false);
    const [glitchLines, setGlitchLines] = useState([]);

    const rootRef = useRef(null);
    const redControls = useAnimation();
    const blueControls = useAnimation();
    const greenControls = useAnimation();
    const timersRef = useRef([]);

    const zeroOffsets = useMemo(() => new Array(sliceCount).fill(0), [sliceCount]);

    // Apply slice offsets via CSS variables (no React re-renders)
    const applySliceOffsets = (offsets) => {
        const el = rootRef.current;
        if (!el) return;
        for (let i = 0; i < offsets.length; i++) {
            el.style.setProperty(`--slice-${i}`, `${offsets[i]}px`);
        }
    };

    useEffect(() => {
        applySliceOffsets(zeroOffsets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliceCount, zeroOffsets]);

    useEffect(() => {
        if (!glitchReady) return undefined;
        let mounted = true;

        const clearAllTimers = () => {
            timersRef.current.forEach(clearTimeout);
            timersRef.current = [];
        };

        const scheduleNextBurst = () => {
            const idle = 200 + Math.random() * 1500;
            const t = window.setTimeout(() => {
                if (!mounted) return;
                runBurst();
            }, idle);
            timersRef.current.push(t);
        };

        const runBurst = async () => {
            const steps = Math.floor(Math.random() * 4) + 4;
            for (let i = 0; i < steps; i++) {
                if (!mounted) return;
                const stepMs = 40 + Math.random() * 120;
                const stepSec = stepMs / 1e3;

                const x = (Math.random() - 0.5) * 14 * (intensity / 8);
                const y = (Math.random() - 0.5) * 6 * (intensity / 8);
                const x2 = -x + (Math.random() - 0.5) * 5;
                const y2 = -y + (Math.random() - 0.5) * 3;
                const skew = (Math.random() - 0.5) * 6;
                const gx = (Math.random() - 0.6) * 10 * (intensity / 8);
                const gy = (Math.random() - 0.6) * 4 * (intensity / 8);
                const gskew = (Math.random() - 0.5) * 8;

                redControls.start({ x, y, skewX: skew, transition: { duration: stepSec, ease: 'linear' } });
                blueControls.start({ x: x2, y: y2, skewX: -skew, transition: { duration: stepSec, ease: 'linear' } });
                greenControls.start({ x: gx, y: gy, skewX: gskew, transition: { duration: stepSec, ease: 'linear' } });

                // Random slice offsets
                const newOffsets = [...zeroOffsets];
                if (Math.random() > 0.5) {
                    const sliceIndex = Math.floor(Math.random() * sliceCount);
                    newOffsets[sliceIndex] = (Math.random() - 0.5) * 30;
                    if (Math.random() > 0.6) {
                        const sliceIndex2 = Math.floor(Math.random() * sliceCount);
                        newOffsets[sliceIndex2] = (Math.random() - 0.5) * 35;
                    }
                }
                applySliceOffsets(newOffsets);

                // Random horizontal lines overlay
                if (Math.random() > 0.4) {
                    const count = Math.floor(Math.random() * 3) + 10;
                    const lines = Array.from({ length: count }).map(() => ({
                        top: 25 + Math.random() * 55,
                        left: 5 + Math.random() * 85,
                        width: 1 + Math.random() * 15,
                        thickness: 1 + Math.floor(Math.random() * 3),
                        opacity: 0.3 + Math.random() * 0.5
                    }));
                    setGlitchLines(lines);
                }

                const gap = stepMs * (0.7 + Math.random() * 0.8);
                await new Promise((resolve) => {
                    const tid = window.setTimeout(resolve, gap);
                    timersRef.current.push(tid);
                });
            }

            // Reset
            redControls.start({ x: 0, y: 0, skewX: 0, transition: { duration: 0.1 } });
            blueControls.start({ x: 0, y: 0, skewX: 0, transition: { duration: 0.1 } });
            greenControls.start({ x: 0, y: 0, skewX: 0, transition: { duration: 0.1 } });
            applySliceOffsets(zeroOffsets);
            setGlitchLines([]);
            scheduleNextBurst();
        };

        scheduleNextBurst();

        return () => {
            mounted = false;
            clearAllTimers();
            redControls.stop();
            blueControls.stop();
            greenControls.stop();
            setGlitchLines([]);
            applySliceOffsets(zeroOffsets);
        };
    }, [glitchReady, intensity, sliceCount, zeroOffsets, redControls, blueControls, greenControls]);

    const renderLayer = (controls, layerColor, key) => (
        <motion.g
            key={key}
            animate={controls}
            initial={{ x: 0, y: 0, skewX: 0 }}
            style={{ color: layerColor, mixBlendMode: 'var(--glitch-blend, screen)', filter: 'saturate(1.2)' }}
        >
            {Array.from({ length: sliceCount }).map((_, i) => (
                <g
                    key={i}
                    style={{
                        clipPath: `inset(${i / sliceCount * 100}% 0 ${100 - (i + 1) / sliceCount * 100}% 0)`,
                        transform: `translateX(var(--slice-${i}, 0px))`,
                        transition: 'transform 0.1s linear'
                    }}
                >
                    <use
                        href={`#${DEFS_ID}`}
                        xlinkHref={`#${DEFS_ID}`}
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
            ))}
        </motion.g>
    );

    return (
        <div ref={rootRef} style={{ position: 'relative', width: '100%', ...style }}>
            {/* base artwork — drawn first */}
            <div style={{ position: 'relative', zIndex: 2 }}>
                <DrawOnScroll
                    paths={paths}
                    viewBoxWidth={viewBoxWidth}
                    viewBoxHeight={viewBoxHeight}
                    sizing="fit"
                    strokeColor={strokeColor}
                    fillColor={fillColor}
                    fillEnabled={fillEnabled}
                    strokeWidth={strokeWidth}
                    onDrawComplete={() => setGlitchReady(true)}
                    onDrawStart={() => setGlitchReady(false)}
                    {...drawProps}
                />
            </div>

            {/* glitch layers — mounted only after the handwriting finished */}
            {glitchReady && (
                <svg
                    viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'visible',
                        pointerEvents: 'none',
                        zIndex: 3
                    }}
                >
                    <defs>
                        <g id={DEFS_ID}>
                            {paths.map((p, i) => <path key={i} d={p.d} />)}
                        </g>
                    </defs>
                    {renderLayer(redControls, colorGlitchOne, 'red')}
                    {renderLayer(blueControls, colorGlitchTwo, 'blue')}
                    {renderLayer(greenControls, colorGlitchThree, 'green')}
                </svg>
            )}

            {/* random horizontal lines overlay */}
            {glitchReady && glitchLines.map((l, idx) => (
                <div
                    key={idx}
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: `${l.top}%`,
                        left: `${l.left}%`,
                        width: `${l.width}%`,
                        maxWidth: '30px',
                        height: `${l.thickness}px`,
                        backgroundColor: strokeColor,
                        opacity: l.opacity,
                        zIndex: 4,
                        pointerEvents: 'none',
                        filter: `drop-shadow(0 0 1px ${strokeColor})`
                    }}
                />
            ))}
        </div>
    );
}
